export const legacyGuideArticles: Record<string, string> = {
  '/guides/first-time-homebuyer': `
<article class="max-w-4xl mx-auto bg-white mt-6 rounded-lg shadow-lg overflow-hidden dark:bg-gray-800">
    <!-- Hero -->
    <div class="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8">
      <p class="text-blue-200 text-sm uppercase tracking-wide mb-2">Complete Guide</p>
      <h1 class="text-3xl md:text-4xl font-bold mb-4">First-Time Homebuyer Guide</h1>
      <p class="text-xl text-blue-100">Everything you need to know about buying your first home, from saving for a down payment to closing day.</p>
    </div>

    <!-- Table of Contents -->
    <div class="p-6 bg-gray-50 border-b">
      <h2 class="font-semibold text-gray-700 mb-3">In This Guide</h2>
      <div class="grid md:grid-cols-2 gap-2 text-sm">
        <a href="#ready" class="text-blue-600 hover:underline dark:text-blue-400">1. Are You Ready to Buy?</a>
        <a href="#afford" class="text-blue-600 hover:underline dark:text-blue-400">2. How Much Can You Afford?</a>
        <a href="#down-payment" class="text-blue-600 hover:underline dark:text-blue-400">3. Saving for a Down Payment</a>
        <a href="#credit" class="text-blue-600 hover:underline dark:text-blue-400">4. Understanding Credit Scores</a>
        <a href="#mortgage-types" class="text-blue-600 hover:underline dark:text-blue-400">5. Types of Mortgages</a>
        <a href="#pre-approval" class="text-blue-600 hover:underline dark:text-blue-400">6. Getting Pre-Approved</a>
        <a href="#house-hunting" class="text-blue-600 hover:underline dark:text-blue-400">7. House Hunting Tips</a>
        <a href="#closing" class="text-blue-600 hover:underline dark:text-blue-400">8. Closing Costs Explained</a>
        <a href="#mistakes" class="text-blue-600 hover:underline dark:text-blue-400">9. Common Mistakes to Avoid</a>
        <a href="#checklist" class="text-blue-600 hover:underline dark:text-blue-400">10. Your Homebuying Checklist</a>
      </div>
    </div>

    <!-- Content -->
    <div class="p-6 md:p-8 space-y-8">
      <!-- Section 1 -->
      <section id="ready">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">1. Are You Ready to Buy?</h2>
        <p class="text-gray-600 mb-4">
          Buying a home is one of the biggest financial decisions you'll make. Before diving in, honestly assess whether you're ready:
        </p>

        <div class="grid md:grid-cols-2 gap-4 mb-4">
          <div class="p-4 bg-green-50 rounded-lg">
            <h3 class="font-semibold text-green-700 mb-2">Signs You're Ready</h3>
            <ul class="text-sm text-gray-600 space-y-1">
              <li>✓ Stable income for 2+ years</li>
              <li>✓ Emergency fund of 3-6 months expenses</li>
              <li>✓ Low debt-to-income ratio (under 36%)</li>
              <li>✓ Good credit score (680+)</li>
              <li>✓ Planning to stay 5+ years</li>
              <li>✓ Have savings beyond the down payment</li>
            </ul>
          </div>

          <div class="p-4 bg-red-50 rounded-lg">
            <h3 class="font-semibold text-red-700 mb-2">Signs to Wait</h3>
            <ul class="text-sm text-gray-600 space-y-1">
              <li>✗ Job instability or recent career change</li>
              <li>✗ High-interest debt (credit cards)</li>
              <li>✗ No emergency fund</li>
              <li>✗ Credit score under 620</li>
              <li>✗ Might relocate in 1-3 years</li>
              <li>✗ Using retirement funds for down payment</li>
            </ul>
          </div>
        </div>

        <div class="p-4 bg-blue-50 rounded-lg">
          <p class="text-sm text-blue-800">
            <strong>Pro Tip:</strong> Renting isn't "throwing money away." If you're not ready, renting gives you flexibility
            to build savings, improve credit, and wait for the right opportunity.
          </p>
        </div>
      </section>

      <!-- Section 2 -->
      <section id="afford">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">2. How Much Can You Afford?</h2>
        <p class="text-gray-600 mb-4">
          Banks will often approve you for more than you should actually spend. Use these guidelines to determine a comfortable budget:
        </p>

        <div class="p-4 bg-gray-50 rounded-lg mb-4">
          <h3 class="font-semibold text-gray-700 mb-3">The 28/36 Rule</h3>
          <div class="grid md:grid-cols-2 gap-4">
            <div>
              <p class="text-3xl font-bold text-blue-600">28%</p>
              <p class="text-sm text-gray-600 dark:text-gray-400">Maximum of gross monthly income on housing costs (mortgage, taxes, insurance)</p>
            </div>
            <div>
              <p class="text-3xl font-bold text-blue-600">36%</p>
              <p class="text-sm text-gray-600 dark:text-gray-400">Maximum of gross monthly income on total debt (housing + car + student loans + credit cards)</p>
            </div>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-gray-100">
              <tr>
                <th class="p-3 text-left">Annual Income</th>
                <th class="p-3 text-left">Max Monthly Payment (28%)</th>
                <th class="p-3 text-left">Approximate Home Price*</th>
              </tr>
            </thead>
            <tbody class="divide-y">
              <tr><td class="p-3">$60,000</td><td class="p-3">$1,400</td><td class="p-3">$200,000 - $225,000</td></tr>
              <tr><td class="p-3">$80,000</td><td class="p-3">$1,867</td><td class="p-3">$265,000 - $305,000</td></tr>
              <tr><td class="p-3">$100,000</td><td class="p-3">$2,333</td><td class="p-3">$335,000 - $380,000</td></tr>
              <tr><td class="p-3">$125,000</td><td class="p-3">$2,917</td><td class="p-3">$415,000 - $475,000</td></tr>
              <tr><td class="p-3">$150,000</td><td class="p-3">$3,500</td><td class="p-3">$500,000 - $570,000</td></tr>
              <tr><td class="p-3">$175,000</td><td class="p-3">$4,083</td><td class="p-3">$585,000 - $665,000</td></tr>
              <tr><td class="p-3">$200,000</td><td class="p-3">$4,667</td><td class="p-3">$670,000 - $760,000</td></tr>
              <tr><td class="p-3">$225,000</td><td class="p-3">$5,250</td><td class="p-3">$750,000 - $855,000</td></tr>
              <tr><td class="p-3">$250,000</td><td class="p-3">$5,833</td><td class="p-3">$835,000 - $945,000</td></tr>
            </tbody>
          </table>
          <p class="text-xs text-gray-500 mt-3 px-3 pb-3 leading-relaxed">*Annual income figures are gross (pre-tax) — that's the basis lenders use to qualify borrowers. Approximate home prices assume a 30-year fixed mortgage, 20% down, and 7% interest. The low end of each range reflects high-tax states (~2% of home value annually in property taxes and insurance); the high end reflects low-tax states (~1%). Use our Mortgage Calculator below for your specific numbers.</p>
        </div>

        <div>
          <p>
            <strong>Reality check on the 28% rule:</strong> The number above is what a lender will <em>approve</em>, not necessarily what's wise to spend. Because 28% is calculated against gross income, federal and state taxes can leave you spending close to half of your actual take-home on housing — especially at higher incomes where marginal rates are steeper. A more conservative personal-finance target is roughly <strong>20% of gross income</strong>, or about <strong>25% of net take-home</strong>. For a $250,000 earner, that's around $4,167/mo instead of $5,833 — meaningful breathing room for retirement, savings, and unexpected expenses.
          </p>
        </div>

        <div>
          <p>
            <strong>Remember:</strong> Just because you're approved for a certain amount doesn't mean you should spend it. A smaller mortgage means more money for retirement, travel, and life's other priorities.
          </p>
        </div>

        <p class="mt-4 text-gray-600">
          <strong>Try our calculator:</strong> Use our <a href="/mortgage-calculator/" class="text-blue-600 hover:underline dark:text-blue-400">Mortgage Calculator</a>
          to see exactly what your monthly payment would be, including taxes and insurance.
        </p>
      </section>

      <!-- Section 3 -->
      <section id="down-payment">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">3. Saving for a Down Payment</h2>
        <p class="text-gray-600 mb-4">
          The down payment is often the biggest hurdle for first-time buyers. Here's what you need to know:
        </p>

        <h3 class="font-semibold text-gray-700 mb-2">How Much Do You Need?</h3>
        <div class="grid md:grid-cols-3 gap-4 mb-4">
          <div class="p-4 bg-gray-50 rounded-lg text-center">
            <p class="text-2xl font-bold text-gray-700">3-5%</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Minimum for conventional loans</p>
            <p class="text-xs text-gray-500">Requires PMI</p>
          </div>
          <div class="p-4 bg-blue-50 rounded-lg text-center">
            <p class="text-2xl font-bold text-blue-700">10-15%</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Good middle ground</p>
            <p class="text-xs text-gray-500">Lower PMI, better rates</p>
          </div>
          <div class="p-4 bg-green-50 rounded-lg text-center">
            <p class="text-2xl font-bold text-green-700">20%</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Traditional target</p>
            <p class="text-xs text-gray-500">No PMI required</p>
          </div>
        </div>

        <h3 class="font-semibold text-gray-700 mb-2">Strategies to Save Faster</h3>
        <ul class="list-disc ml-6 text-gray-600 space-y-2 mb-4">
          <li><strong>Automate savings:</strong> Set up automatic transfers to a dedicated "house fund" account</li>
          <li><strong>Cut big expenses:</strong> Downgrade your car, find a cheaper apartment, reduce subscriptions</li>
          <li><strong>Increase income:</strong> Side hustles, freelancing, or asking for a raise</li>
          <li><strong>Bank windfalls:</strong> Tax refunds, bonuses, and gifts go straight to savings</li>
          <li><strong>High-yield savings:</strong> Earn 4-5% APY while saving (not in stocks—too volatile for short-term goals)</li>
        </ul>

        <h3 class="font-semibold text-gray-700 mb-2">Down Payment Assistance Programs</h3>
        <p class="text-gray-600 mb-2">Many first-time buyers don't realize help is available:</p>
        <ul class="list-disc ml-6 text-gray-600 space-y-1">
          <li>FHA loans require only 3.5% down</li>
          <li>VA loans offer 0% down for veterans</li>
          <li>USDA loans offer 0% down in rural areas</li>
          <li>State and local programs offer grants or low-interest loans</li>
          <li>Employer programs (some companies help with down payments)</li>
        </ul>
      </section>

      <!-- Section 4 -->
      <section id="credit">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">4. Understanding Credit Scores</h2>
        <p class="text-gray-600 mb-4">
          Your credit score significantly impacts your mortgage rate. A higher score means lower monthly payments.
        </p>

        <div class="overflow-x-auto mb-4">
          <table class="w-full text-sm">
            <thead class="bg-gray-100">
              <tr>
                <th class="p-3 text-left">Credit Score</th>
                <th class="p-3 text-left">Rating</th>
                <th class="p-3 text-left">Typical Impact</th>
              </tr>
            </thead>
            <tbody class="divide-y">
              <tr class="bg-red-50"><td class="p-3">300-579</td><td class="p-3">Poor</td><td class="p-3">Difficult to get approved</td></tr>
              <tr class="bg-yellow-50"><td class="p-3">580-669</td><td class="p-3">Fair</td><td class="p-3">Higher rates, FHA loans available</td></tr>
              <tr class="bg-blue-50"><td class="p-3">670-739</td><td class="p-3">Good</td><td class="p-3">Competitive rates available</td></tr>
              <tr class="bg-green-50"><td class="p-3">740-799</td><td class="p-3">Very Good</td><td class="p-3">Better than average rates</td></tr>
              <tr class="bg-green-100"><td class="p-3">800-850</td><td class="p-3">Excellent</td><td class="p-3">Best rates available</td></tr>
            </tbody>
          </table>
        </div>

        <h3 class="font-semibold text-gray-700 mb-2">How to Improve Your Credit</h3>
        <ul class="list-disc ml-6 text-gray-600 space-y-1">
          <li><strong>Pay bills on time</strong> - Payment history is 35% of your score</li>
          <li><strong>Keep credit utilization under 30%</strong> - Ideally under 10%</li>
          <li><strong>Don't close old accounts</strong> - Length of credit history matters</li>
          <li><strong>Limit new credit applications</strong> - Each inquiry dings your score</li>
          <li><strong>Check for errors</strong> - Dispute any inaccuracies on your credit report</li>
        </ul>

        <div class="mt-4 p-4 bg-blue-50 rounded-lg">
          <p class="text-sm text-blue-800">
            <strong>The Rate Difference:</strong> On a $400,000 loan, improving your score from 680 to 740 could save you
            0.5% on your rate—that's $100+/month or $36,000+ over the life of the loan.
          </p>
        </div>
      </section>

      <!-- Section 5 -->
      <section id="mortgage-types">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">5. Types of Mortgages</h2>

        <div class="space-y-4">
          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700">Conventional Loans</h3>
            <p class="text-sm text-gray-600 mt-1">
              Not government-backed. Require 3-20% down. Best rates for buyers with good credit (700+).
              PMI required if down payment is less than 20%.
            </p>
          </div>

          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700">FHA Loans</h3>
            <p class="text-sm text-gray-600 mt-1">
              Government-insured. Only 3.5% down with 580+ credit. More lenient requirements but requires
              mortgage insurance for the life of the loan.
            </p>
          </div>

          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700">VA Loans</h3>
            <p class="text-sm text-gray-600 mt-1">
              For veterans and active military. 0% down payment, no PMI, competitive rates.
              One of the best loan programs available.
            </p>
          </div>

          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700">USDA Loans</h3>
            <p class="text-sm text-gray-600 mt-1">
              For rural and suburban homebuyers. 0% down payment. Income limits apply.
              Check USDA eligibility maps for your area.
            </p>
          </div>

          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700">Fixed vs. Adjustable Rate</h3>
            <p class="text-sm text-gray-600 mt-1">
              <strong>Fixed-rate:</strong> Same rate for 15 or 30 years. Predictable payments. Best for long-term owners.<br>
              <strong>ARM:</strong> Lower initial rate that adjusts after 5-7 years. Risky if rates rise. Only consider if you'll move before adjustment.
            </p>
          </div>
        </div>
      </section>

      <!-- Section 6 -->
      <section id="pre-approval">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">6. Getting Pre-Approved</h2>
        <p class="text-gray-600 mb-4">
          Pre-approval is essential before house hunting. It tells sellers you're a serious buyer with financing ready.
        </p>

        <h3 class="font-semibold text-gray-700 mb-2">Documents You'll Need</h3>
        <ul class="list-disc ml-6 text-gray-600 space-y-1 mb-4">
          <li>Pay stubs (last 2-3 months)</li>
          <li>W-2s and tax returns (last 2 years)</li>
          <li>Bank statements (last 2-3 months)</li>
          <li>Investment account statements</li>
          <li>ID and Social Security number</li>
          <li>Employment verification</li>
        </ul>

        <div class="p-4 bg-yellow-50 rounded-lg">
          <p class="text-sm text-yellow-800">
            <strong>Important:</strong> Get quotes from at least 3 lenders. Rates can vary significantly.
            All credit inquiries within a 14-45 day window count as one inquiry for your credit score.
          </p>
        </div>
      </section>

      <!-- Section 7 -->
      <section id="house-hunting">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">7. House Hunting Tips</h2>

        <h3 class="font-semibold text-gray-700 mb-2">Make a Must-Have vs. Nice-to-Have List</h3>
        <div class="grid md:grid-cols-2 gap-4 mb-4">
          <div class="p-4 bg-gray-50 rounded-lg">
            <p class="font-medium text-gray-700 mb-2">Can't Change:</p>
            <ul class="text-sm text-gray-600 list-disc ml-4">
              <li>Location/neighborhood</li>
              <li>School district</li>
              <li>Lot size</li>
              <li>Commute time</li>
              <li>Basic structure/layout</li>
            </ul>
          </div>
          <div class="p-4 bg-gray-50 rounded-lg">
            <p class="font-medium text-gray-700 mb-2">Can Change Later:</p>
            <ul class="text-sm text-gray-600 list-disc ml-4">
              <li>Paint colors</li>
              <li>Flooring</li>
              <li>Kitchen updates</li>
              <li>Landscaping</li>
              <li>Fixtures and finishes</li>
            </ul>
          </div>
        </div>

        <h3 class="font-semibold text-gray-700 mb-2">Red Flags to Watch For</h3>
        <ul class="list-disc ml-6 text-gray-600 space-y-1">
          <li>Water stains on ceilings or walls</li>
          <li>Cracks in foundation</li>
          <li>Musty smells (could indicate mold)</li>
          <li>Uneven floors</li>
          <li>DIY electrical or plumbing work</li>
          <li>Neighborhood with many for-sale signs</li>
        </ul>
      </section>

      <!-- Section 8 -->
      <section id="closing">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">8. Closing Costs Explained</h2>
        <p class="text-gray-600 mb-4">
          Beyond the down payment, closing costs typically run 2-5% of the home price. Budget for these:
        </p>

        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-gray-100">
              <tr>
                <th class="p-3 text-left">Cost Type</th>
                <th class="p-3 text-left">Typical Amount</th>
              </tr>
            </thead>
            <tbody class="divide-y">
              <tr><td class="p-3">Loan origination fee</td><td class="p-3">0.5-1% of loan</td></tr>
              <tr><td class="p-3">Appraisal</td><td class="p-3">$300-$700</td></tr>
              <tr><td class="p-3">Home inspection</td><td class="p-3">$300-$500</td></tr>
              <tr><td class="p-3">Title insurance</td><td class="p-3">$1,000-$3,000</td></tr>
              <tr><td class="p-3">Attorney fees</td><td class="p-3">$500-$1,500</td></tr>
              <tr><td class="p-3">Prepaid taxes/insurance</td><td class="p-3">2-6 months worth</td></tr>
              <tr><td class="p-3">Recording fees</td><td class="p-3">$50-$250</td></tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 p-4 bg-green-50 rounded-lg">
          <p class="text-sm text-green-800">
            <strong>Negotiation tip:</strong> You can sometimes negotiate for the seller to pay some closing costs,
            especially in a buyer's market. Ask your agent about "seller concessions."
          </p>
        </div>
      </section>

      <!-- Section 9 -->
      <section id="mistakes">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">9. Common Mistakes to Avoid</h2>

        <div class="space-y-3">
          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Making big purchases before closing</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Don't buy a car, furniture, or anything on credit. Lenders re-check your finances before closing.</p>
          </div>

          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Skipping the home inspection</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Never waive inspection to win a bidding war. A $400 inspection could save you $40,000 in repairs.</p>
          </div>

          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Emptying your savings for the down payment</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Keep an emergency fund. Homes always have unexpected expenses.</p>
          </div>

          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Buying at the top of your budget</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Just because you're approved for $500K doesn't mean you should spend it. Leave room for life.</p>
          </div>

          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Forgetting about ongoing costs</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Property taxes, insurance, maintenance, HOA fees, and utilities add up. Budget 1-2% of home value yearly for maintenance.</p>
          </div>
        </div>
      </section>

      <!-- Section 10 -->
      <section id="checklist">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">10. Your Homebuying Checklist</h2>

        <div class="space-y-4">
          <div>
            <h3 class="font-semibold text-gray-700 mb-2">6-12 Months Before</h3>
            <ul class="space-y-1 text-gray-600">
              <li>☐ Check and improve credit score</li>
              <li>☐ Save for down payment and closing costs</li>
              <li>☐ Build emergency fund (separate from down payment)</li>
              <li>☐ Research neighborhoods and price ranges</li>
              <li>☐ Calculate what you can afford</li>
            </ul>
          </div>

          <div>
            <h3 class="font-semibold text-gray-700 mb-2">2-3 Months Before</h3>
            <ul class="space-y-1 text-gray-600">
              <li>☐ Get pre-approved (compare 3+ lenders)</li>
              <li>☐ Find a real estate agent</li>
              <li>☐ Make your must-have list</li>
              <li>☐ Start touring homes</li>
            </ul>
          </div>

          <div>
            <h3 class="font-semibold text-gray-700 mb-2">Making an Offer</h3>
            <ul class="space-y-1 text-gray-600">
              <li>☐ Research comparable sales</li>
              <li>☐ Submit offer with pre-approval letter</li>
              <li>☐ Negotiate terms</li>
              <li>☐ Sign purchase agreement</li>
            </ul>
          </div>

          <div>
            <h3 class="font-semibold text-gray-700 mb-2">Under Contract</h3>
            <ul class="space-y-1 text-gray-600">
              <li>☐ Schedule home inspection</li>
              <li>☐ Complete loan application</li>
              <li>☐ Get homeowner's insurance quotes</li>
              <li>☐ Review inspection report and negotiate repairs</li>
              <li>☐ Complete appraisal</li>
              <li>☐ Do final walkthrough</li>
            </ul>
          </div>

          <div>
            <h3 class="font-semibold text-gray-700 mb-2">Closing Day</h3>
            <ul class="space-y-1 text-gray-600">
              <li>☐ Review closing disclosure (3 days before)</li>
              <li>☐ Wire funds or bring certified check</li>
              <li>☐ Sign documents</li>
              <li>☐ Get your keys!</li>
            </ul>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <div class="mt-8 p-6 bg-blue-50 rounded-lg text-center">
        <h3 class="text-xl font-bold text-blue-700 mb-2">Ready to Crunch the Numbers?</h3>
        <p class="text-gray-600 mb-4">Use our calculators to plan your home purchase:</p>
        <div class="flex flex-wrap justify-center gap-3">
          <a href="/mortgage-calculator/" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Mortgage Calculator
          </a>
          <a href="/rent-vs-buy/" class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Rent vs Buy Calculator
          </a>
        </div>
      </div>
    </div>
  </article>
`,
  '/guides/investing-basics': `
<article class="max-w-4xl mx-auto bg-white mt-6 rounded-lg shadow-lg overflow-hidden dark:bg-gray-800">
    <!-- Hero -->
    <div class="bg-gradient-to-r from-green-600 to-green-700 text-white p-8">
      <p class="text-green-200 text-sm uppercase tracking-wide mb-2">Beginner's Guide</p>
      <h1 class="text-3xl md:text-4xl font-bold mb-4">Investing Basics for Beginners</h1>
      <p class="text-xl text-green-100">Everything you need to know to start building wealth through investing.</p>
    </div>

    <!-- Content -->
    <div class="p-6 md:p-8 space-y-8">
      <!-- Intro -->
      <section>
        <p class="text-lg text-gray-600 mb-4 dark:text-gray-400">
          Investing can seem intimidating, but it doesn't have to be. This guide breaks down the essentials so you can
          start building wealth—even if you're starting with just a few dollars.
        </p>

        <div class="p-4 bg-green-50 rounded-lg">
          <p class="text-green-800">
            <strong>The most important thing:</strong> Time in the market beats timing the market.
            Starting early—even with small amounts—matters more than waiting until you have "enough" to invest.
          </p>
        </div>
      </section>

      <!-- Why Invest -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Why You Need to Invest</h2>

        <p class="text-gray-600 mb-4">
          Keeping all your money in a savings account feels safe, but inflation silently erodes its value.
          At 3% inflation, $10,000 today will only have the buying power of $7,400 in 10 years.
        </p>

        <div class="grid md:grid-cols-2 gap-4 mb-4">
          <div class="p-4 bg-red-50 rounded-lg">
            <h3 class="font-semibold text-red-700">Savings Account @ 4%</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">$10,000 invested for 30 years</p>
            <p class="text-2xl font-bold text-red-600 mt-2">$32,434</p>
          </div>
          <div class="p-4 bg-green-50 rounded-lg">
            <h3 class="font-semibold text-green-700">Stock Market @ 10%</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">$10,000 invested for 30 years</p>
            <p class="text-2xl font-bold text-green-600 mt-2">$174,494</p>
          </div>
        </div>

        <p class="text-gray-600 dark:text-gray-400">
          That's the power of compound growth. Your money makes money, and then that money makes more money.
        </p>
      </section>

      <!-- Types of Investments -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Types of Investments</h2>

        <div class="space-y-4">
          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700 text-lg">Stocks</h3>
            <p class="text-gray-600 mt-2">
              Ownership shares in a company. When the company does well, your shares increase in value.
              Higher risk, higher potential returns. Individual stocks are volatile—one company can go to zero.
            </p>
            <p class="text-sm text-gray-500 mt-2">
              <strong>Risk level:</strong> High | <strong>Historical return:</strong> ~10% annually
            </p>
          </div>

          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700 text-lg">Bonds</h3>
            <p class="text-gray-600 mt-2">
              Loans you make to governments or corporations. They pay you interest over time and return your principal at maturity.
              Lower risk, lower returns. Good for stability and income.
            </p>
            <p class="text-sm text-gray-500 mt-2">
              <strong>Risk level:</strong> Low to Medium | <strong>Historical return:</strong> ~4-6% annually
            </p>
          </div>

          <div class="p-4 border rounded-lg bg-green-50">
            <h3 class="font-semibold text-green-700 text-lg">Index Funds & ETFs (Recommended for Beginners)</h3>
            <p class="text-gray-600 mt-2">
              Baskets of hundreds or thousands of stocks/bonds. One purchase gives you instant diversification.
              Low fees, no need to pick winners. Warren Buffett recommends index funds for most investors.
            </p>
            <p class="text-sm text-gray-500 mt-2">
              <strong>Risk level:</strong> Medium | <strong>Historical return:</strong> ~7-10% annually
            </p>
            <div class="mt-3 p-3 bg-white rounded">
              <p class="text-sm text-gray-700">
                <strong>Popular options:</strong> VTI (Total US Stock Market), VXUS (International), BND (Bonds), VT (Total World)
              </p>
            </div>
          </div>

          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700 text-lg">Real Estate</h3>
            <p class="text-gray-600 mt-2">
              Physical property or REITs (Real Estate Investment Trusts). Provides income through rent and potential appreciation.
              Physical property requires significant capital and management. REITs offer real estate exposure without being a landlord.
            </p>
            <p class="text-sm text-gray-500 mt-2">
              <strong>Risk level:</strong> Medium | <strong>Historical return:</strong> ~8-12% annually
            </p>
          </div>
        </div>
      </section>

      <!-- Account Types -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Investment Account Types</h2>

        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-gray-100">
              <tr>
                <th class="p-3 text-left">Account Type</th>
                <th class="p-3 text-left">Tax Benefit</th>
                <th class="p-3 text-left">Best For</th>
              </tr>
            </thead>
            <tbody class="divide-y">
              <tr>
                <td class="p-3 font-medium">401(k)</td>
                <td class="p-3">Pre-tax contributions, tax-deferred growth</td>
                <td class="p-3">Employer match, high contribution limits ($23,500)</td>
              </tr>
              <tr>
                <td class="p-3 font-medium">Roth IRA</td>
                <td class="p-3">After-tax contributions, tax-free withdrawals</td>
                <td class="p-3">Young investors expecting higher future taxes</td>
              </tr>
              <tr>
                <td class="p-3 font-medium">Traditional IRA</td>
                <td class="p-3">Tax-deductible contributions, tax-deferred growth</td>
                <td class="p-3">Those without 401(k) access</td>
              </tr>
              <tr>
                <td class="p-3 font-medium">HSA</td>
                <td class="p-3">Triple tax advantage (deductible, grows tax-free, tax-free withdrawals for medical)</td>
                <td class="p-3">Those with high-deductible health plans</td>
              </tr>
              <tr>
                <td class="p-3 font-medium">Taxable Brokerage</td>
                <td class="p-3">No special tax benefits, but no restrictions</td>
                <td class="p-3">Goals before retirement, maxed out tax-advantaged accounts</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 p-4 bg-blue-50 rounded-lg">
          <h3 class="font-semibold text-blue-700 mb-2">Recommended Order for Investing</h3>
          <ol class="list-decimal ml-6 text-gray-600 space-y-1">
            <li>401(k) up to employer match (free money!)</li>
            <li>Pay off high-interest debt (credit cards)</li>
            <li>Build emergency fund (3-6 months expenses)</li>
            <li>Max out Roth IRA ($7,000/year)</li>
            <li>Max out 401(k) ($23,500/year)</li>
            <li>HSA if eligible ($4,150 individual / $8,300 family)</li>
            <li>Taxable brokerage for everything else</li>
          </ol>
        </div>
      </section>

      <!-- How to Start -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">How to Start Investing Today</h2>

        <div class="space-y-4">
          <div class="flex items-start">
            <div class="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">1</div>
            <div>
              <h3 class="font-semibold text-gray-700">Open an Account</h3>
              <p class="text-gray-600 text-sm">
                Use your employer's 401(k) or open a Roth IRA at Fidelity, Vanguard, or Schwab. Takes about 15 minutes.
              </p>
            </div>
          </div>

          <div class="flex items-start">
            <div class="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">2</div>
            <div>
              <h3 class="font-semibold text-gray-700">Choose Your Investments</h3>
              <p class="text-gray-600 text-sm">
                Start simple: a target-date fund (set it and forget it) or a total market index fund like VTI or FXAIX.
              </p>
            </div>
          </div>

          <div class="flex items-start">
            <div class="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">3</div>
            <div>
              <h3 class="font-semibold text-gray-700">Set Up Automatic Contributions</h3>
              <p class="text-gray-600 text-sm">
                Automate transfers from every paycheck. Pay yourself first before you can spend it.
              </p>
            </div>
          </div>

          <div class="flex items-start">
            <div class="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">4</div>
            <div>
              <h3 class="font-semibold text-gray-700">Don't Touch It</h3>
              <p class="text-gray-600 text-sm">
                Ignore the market news. Don't panic sell during drops. Time in the market beats timing the market.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Common Mistakes -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Common Beginner Mistakes</h2>

        <div class="space-y-3">
          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Waiting until you "know enough"</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">You'll never feel ready. Start with a simple index fund and learn as you go.</p>
          </div>

          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Trying to pick individual stocks</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Even professionals rarely beat index funds long-term. Don't try to be the exception.</p>
          </div>

          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Panic selling during market drops</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">Markets recover. Selling locks in your losses. Stay the course.</p>
          </div>

          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Paying high fees</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">A 1% fee vs 0.03% fee costs you hundreds of thousands over a lifetime. Choose low-cost index funds.</p>
          </div>

          <div class="p-4 bg-red-50 rounded-lg">
            <p class="font-medium text-red-700">Not taking advantage of employer match</p>
            <p class="text-sm text-gray-600 dark:text-gray-400">If your employer matches 401(k) contributions, that's a 50-100% instant return. Don't leave free money on the table.</p>
          </div>
        </div>
      </section>

      <!-- Simple Portfolio -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Simple Beginner Portfolios</h2>

        <div class="grid md:grid-cols-2 gap-4">
          <div class="p-4 bg-gray-50 rounded-lg">
            <h3 class="font-semibold text-gray-700 mb-2">The One-Fund Portfolio</h3>
            <p class="text-sm text-gray-600 mb-3">Simplest option. Set it and forget it.</p>
            <div class="p-3 bg-white rounded">
              <p class="text-sm"><strong>100%</strong> Target Date Fund (e.g., Vanguard Target Retirement 2055)</p>
            </div>
            <p class="text-xs text-gray-500 mt-2">Automatically adjusts allocation as you age.</p>
          </div>

          <div class="p-4 bg-gray-50 rounded-lg">
            <h3 class="font-semibold text-gray-700 mb-2">The Three-Fund Portfolio</h3>
            <p class="text-sm text-gray-600 mb-3">Classic approach. Slightly more control.</p>
            <div class="p-3 bg-white rounded space-y-1">
              <p class="text-sm"><strong>60%</strong> US Total Stock Market (VTI)</p>
              <p class="text-sm"><strong>30%</strong> International Stocks (VXUS)</p>
              <p class="text-sm"><strong>10%</strong> Bonds (BND)</p>
            </div>
            <p class="text-xs text-gray-500 mt-2">Adjust bond % higher as you approach retirement.</p>
          </div>
        </div>
      </section>

      <!-- Glossary -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Key Terms to Know</h2>

        <dl class="space-y-3">
          <div>
            <dt class="font-semibold text-gray-700">Compound Interest</dt>
            <dd class="text-gray-600 text-sm">Earning returns on your returns. The snowball effect that builds wealth over time.</dd>
          </div>
          <div>
            <dt class="font-semibold text-gray-700">Diversification</dt>
            <dd class="text-gray-600 text-sm">Spreading investments across many assets to reduce risk. "Don't put all your eggs in one basket."</dd>
          </div>
          <div>
            <dt class="font-semibold text-gray-700">Expense Ratio</dt>
            <dd class="text-gray-600 text-sm">Annual fee charged by funds. Look for under 0.20%. Index funds are often 0.03-0.10%.</dd>
          </div>
          <div>
            <dt class="font-semibold text-gray-700">Dollar-Cost Averaging</dt>
            <dd class="text-gray-600 text-sm">Investing a fixed amount regularly, regardless of market conditions. Reduces impact of volatility.</dd>
          </div>
          <div>
            <dt class="font-semibold text-gray-700">Asset Allocation</dt>
            <dd class="text-gray-600 text-sm">How you divide your money between stocks, bonds, and other investments based on your goals and risk tolerance.</dd>
          </div>
        </dl>
      </section>

      <!-- CTA -->
      <div class="mt-8 p-6 bg-green-50 rounded-lg text-center">
        <h3 class="text-xl font-bold text-green-700 mb-2">Ready to See Your Money Grow?</h3>
        <p class="text-gray-600 mb-4">Use our FIRE calculator to project your investment growth:</p>
        <a href="/" class="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700">
          Investment Calculator
        </a>
      </div>
    </div>
  </article>
`,
  '/guides/debt-payoff-strategies': `
<article class="max-w-4xl mx-auto bg-white mt-6 rounded-lg shadow-lg overflow-hidden dark:bg-gray-800">
    <!-- Hero -->
    <div class="bg-gradient-to-r from-red-600 to-red-700 text-white p-8">
      <p class="text-red-200 text-sm uppercase tracking-wide mb-2">Strategy Guide</p>
      <h1 class="text-3xl md:text-4xl font-bold mb-4">Debt Payoff Strategies That Actually Work</h1>
      <p class="text-xl text-red-100">Proven methods to eliminate debt and take control of your financial future.</p>
    </div>

    <!-- Content -->
    <div class="p-6 md:p-8 space-y-8">
      <!-- Intro -->
      <section>
        <p class="text-lg text-gray-600 mb-4 dark:text-gray-400">
          Debt can feel overwhelming, but thousands of people pay off massive amounts every year using proven strategies.
          This guide will help you choose the right approach and create an actionable plan.
        </p>

        <div class="p-4 bg-blue-50 rounded-lg">
          <p class="text-blue-800">
            <strong>Key insight:</strong> The best debt payoff strategy is the one you'll stick with.
            Choose based on your psychology, not just math.
          </p>
        </div>
      </section>

      <!-- Know Your Debt -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Step 1: Know Your Debt</h2>
        <p class="text-gray-600 mb-4">
          Before choosing a strategy, you need a complete picture. List every debt:
        </p>

        <div class="overflow-x-auto mb-4">
          <table class="w-full text-sm">
            <thead class="bg-gray-100">
              <tr>
                <th class="p-3 text-left">Debt</th>
                <th class="p-3 text-right">Balance</th>
                <th class="p-3 text-right">Interest Rate</th>
                <th class="p-3 text-right">Minimum Payment</th>
              </tr>
            </thead>
            <tbody class="divide-y">
              <tr><td class="p-3">Credit Card #1</td><td class="p-3 text-right">$_____</td><td class="p-3 text-right">____%</td><td class="p-3 text-right">$_____</td></tr>
              <tr><td class="p-3">Credit Card #2</td><td class="p-3 text-right">$_____</td><td class="p-3 text-right">____%</td><td class="p-3 text-right">$_____</td></tr>
              <tr><td class="p-3">Car Loan</td><td class="p-3 text-right">$_____</td><td class="p-3 text-right">____%</td><td class="p-3 text-right">$_____</td></tr>
              <tr><td class="p-3">Student Loans</td><td class="p-3 text-right">$_____</td><td class="p-3 text-right">____%</td><td class="p-3 text-right">$_____</td></tr>
              <tr><td class="p-3">Personal Loan</td><td class="p-3 text-right">$_____</td><td class="p-3 text-right">____%</td><td class="p-3 text-right">$_____</td></tr>
              <tr class="bg-gray-50 font-semibold"><td class="p-3">TOTAL</td><td class="p-3 text-right">$_____</td><td class="p-3 text-right">-</td><td class="p-3 text-right">$_____</td></tr>
            </tbody>
          </table>
        </div>

        <p class="text-gray-600 dark:text-gray-400">
          Use our <a href="/debt-payoff/" class="text-blue-600 hover:underline dark:text-blue-400">Debt Payoff Calculator</a> to input your debts and see your payoff timeline.
        </p>
      </section>

      <!-- The Two Main Methods -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Step 2: Choose Your Strategy</h2>

        <div class="grid md:grid-cols-2 gap-6 mb-6">
          <!-- Avalanche -->
          <div class="p-6 border-2 border-blue-500 rounded-lg">
            <h3 class="text-xl font-bold text-blue-700 mb-2">Avalanche Method</h3>
            <p class="text-sm text-gray-500 mb-4">Pay highest interest rate first</p>

            <p class="text-gray-600 mb-4">
              Order debts by interest rate (highest to lowest). Make minimum payments on all debts,
              then put every extra dollar toward the highest-rate debt.
            </p>

            <div class="space-y-2 mb-4">
              <div class="flex items-center text-sm">
                <span class="text-green-500 mr-2">✓</span>
                <span class="text-gray-600 dark:text-gray-400">Saves the most money on interest</span>
              </div>
              <div class="flex items-center text-sm">
                <span class="text-green-500 mr-2">✓</span>
                <span class="text-gray-600 dark:text-gray-400">Mathematically optimal</span>
              </div>
              <div class="flex items-center text-sm">
                <span class="text-green-500 mr-2">✓</span>
                <span class="text-gray-600 dark:text-gray-400">Pays off debt fastest overall</span>
              </div>
              <div class="flex items-center text-sm">
                <span class="text-red-500 mr-2">✗</span>
                <span class="text-gray-600 dark:text-gray-400">May take longer to see first win</span>
              </div>
            </div>

            <p class="text-sm text-blue-700 font-medium">
              Best for: Analytical people who are motivated by saving money
            </p>
          </div>

          <!-- Snowball -->
          <div class="p-6 border-2 border-green-500 rounded-lg">
            <h3 class="text-xl font-bold text-green-700 mb-2">Snowball Method</h3>
            <p class="text-sm text-gray-500 mb-4">Pay smallest balance first</p>

            <p class="text-gray-600 mb-4">
              Order debts by balance (smallest to largest). Make minimum payments on all debts,
              then throw everything extra at the smallest balance.
            </p>

            <div class="space-y-2 mb-4">
              <div class="flex items-center text-sm">
                <span class="text-green-500 mr-2">✓</span>
                <span class="text-gray-600 dark:text-gray-400">Quick wins build momentum</span>
              </div>
              <div class="flex items-center text-sm">
                <span class="text-green-500 mr-2">✓</span>
                <span class="text-gray-600 dark:text-gray-400">Psychological boost from eliminating debts</span>
              </div>
              <div class="flex items-center text-sm">
                <span class="text-green-500 mr-2">✓</span>
                <span class="text-gray-600 dark:text-gray-400">Simplifies bills faster</span>
              </div>
              <div class="flex items-center text-sm">
                <span class="text-red-500 mr-2">✗</span>
                <span class="text-gray-600 dark:text-gray-400">May pay more total interest</span>
              </div>
            </div>

            <p class="text-sm text-green-700 font-medium">
              Best for: People who need motivation and quick wins to stay on track
            </p>
          </div>
        </div>

        <!-- Example -->
        <div class="p-4 bg-gray-50 rounded-lg">
          <h3 class="font-semibold text-gray-700 mb-3">Example: Same Debts, Different Order</h3>
          <div class="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p class="font-medium text-blue-700 mb-2">Avalanche Order:</p>
              <ol class="list-decimal ml-4 text-gray-600 space-y-1">
                <li>Credit Card: $3,000 @ 22%</li>
                <li>Personal Loan: $8,000 @ 12%</li>
                <li>Car Loan: $15,000 @ 6%</li>
                <li>Student Loan: $25,000 @ 5%</li>
              </ol>
            </div>
            <div>
              <p class="font-medium text-green-700 mb-2">Snowball Order:</p>
              <ol class="list-decimal ml-4 text-gray-600 space-y-1">
                <li>Credit Card: $3,000 @ 22%</li>
                <li>Personal Loan: $8,000 @ 12%</li>
                <li>Car Loan: $15,000 @ 6%</li>
                <li>Student Loan: $25,000 @ 5%</li>
              </ol>
              <p class="text-xs text-gray-500 mt-1">(Same in this case since smallest also has highest rate!)</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Find Extra Money -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Step 3: Find Extra Money</h2>
        <p class="text-gray-600 mb-4">
          The more you can throw at debt, the faster you're free. Here's where to find extra cash:
        </p>

        <div class="grid md:grid-cols-2 gap-4">
          <div class="p-4 bg-green-50 rounded-lg">
            <h3 class="font-semibold text-green-700 mb-2">Cut Expenses</h3>
            <ul class="text-sm text-gray-600 space-y-1">
              <li>• Cancel unused subscriptions</li>
              <li>• Cook at home more</li>
              <li>• Switch to cheaper phone plan</li>
              <li>• Negotiate bills (insurance, cable)</li>
              <li>• Use the library instead of buying</li>
              <li>• Pause non-essential spending</li>
            </ul>
          </div>

          <div class="p-4 bg-blue-50 rounded-lg">
            <h3 class="font-semibold text-blue-700 mb-2">Increase Income</h3>
            <ul class="text-sm text-gray-600 space-y-1">
              <li>• Ask for a raise</li>
              <li>• Start a side hustle</li>
              <li>• Sell stuff you don't need</li>
              <li>• Freelance your skills</li>
              <li>• Drive for Uber/DoorDash</li>
              <li>• Pick up overtime shifts</li>
            </ul>
          </div>
        </div>

        <div class="mt-4 p-4 bg-yellow-50 rounded-lg">
          <p class="text-sm text-yellow-800">
            <strong>Pro tip:</strong> Dedicate all "extra" money to debt: tax refunds, birthday money, bonuses, rebates.
            These windfalls can knock out months of progress in one payment.
          </p>
        </div>
      </section>

      <!-- Other Strategies -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Advanced Strategies</h2>

        <div class="space-y-4">
          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700">Balance Transfer Cards</h3>
            <p class="text-gray-600 text-sm mt-2">
              Transfer high-interest credit card debt to a card with 0% intro APR (typically 15-21 months).
              Pay no interest during the promo period. Watch for transfer fees (usually 3-5%).
            </p>
            <p class="text-xs text-gray-500 mt-2">
              <strong>Best for:</strong> Good credit, disciplined payers who will pay off before promo ends.
            </p>
          </div>

          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700">Debt Consolidation Loan</h3>
            <p class="text-gray-600 text-sm mt-2">
              Take out a personal loan at a lower rate to pay off multiple high-interest debts.
              Simplifies payments and may reduce interest. Be careful not to rack up new credit card debt.
            </p>
            <p class="text-xs text-gray-500 mt-2">
              <strong>Best for:</strong> Multiple debts at high rates, good credit to qualify for lower rate.
            </p>
          </div>

          <div class="p-4 border rounded-lg">
            <h3 class="font-semibold text-gray-700">Negotiate with Creditors</h3>
            <p class="text-gray-600 text-sm mt-2">
              Call your credit card companies and ask for a lower interest rate. Many will reduce it by 2-5%
              if you have a good payment history. It takes 5 minutes and saves real money.
            </p>
          </div>

          <div class="p-4 border rounded-lg bg-red-50">
            <h3 class="font-semibold text-red-700">Avoid: Debt Settlement Companies</h3>
            <p class="text-gray-600 text-sm mt-2">
              These companies charge high fees and often damage your credit. They tell you to stop paying
              (wrecking your credit) while they negotiate. You can negotiate yourself for free.
            </p>
          </div>
        </div>
      </section>

      <!-- Staying Motivated -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">Staying Motivated</h2>

        <div class="space-y-3">
          <div class="flex items-start">
            <div class="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 text-sm">1</div>
            <div>
              <h3 class="font-semibold text-gray-700">Track Your Progress</h3>
              <p class="text-gray-600 text-sm">Use a spreadsheet, app, or our calculator. Watching the numbers drop is motivating.</p>
            </div>
          </div>

          <div class="flex items-start">
            <div class="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 text-sm">2</div>
            <div>
              <h3 class="font-semibold text-gray-700">Celebrate Milestones</h3>
              <p class="text-gray-600 text-sm">Paid off a card? Treat yourself (cheaply). Small rewards prevent burnout.</p>
            </div>
          </div>

          <div class="flex items-start">
            <div class="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 text-sm">3</div>
            <div>
              <h3 class="font-semibold text-gray-700">Visualize Your Debt-Free Life</h3>
              <p class="text-gray-600 text-sm">What will you do with that monthly payment once debt is gone? Keep that vision front and center.</p>
            </div>
          </div>

          <div class="flex items-start">
            <div class="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 text-sm">4</div>
            <div>
              <h3 class="font-semibold text-gray-700">Find Your Community</h3>
              <p class="text-gray-600 text-sm">Join r/personalfinance, r/debtfree, or find an accountability partner. You're not alone.</p>
            </div>
          </div>

          <div class="flex items-start">
            <div class="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 text-sm">5</div>
            <div>
              <h3 class="font-semibold text-gray-700">Don't Add New Debt</h3>
              <p class="text-gray-600 text-sm">Put the credit cards in a drawer (or freeze them in ice). Cash or debit only during payoff.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- After Debt -->
      <section>
        <h2 class="text-2xl font-bold text-gray-800 mb-4">After You're Debt-Free</h2>
        <p class="text-gray-600 mb-4">
          Congratulations! Now redirect that money toward building wealth:
        </p>

        <ol class="list-decimal ml-6 text-gray-600 space-y-2">
          <li><strong>Build a full emergency fund</strong> - 3-6 months of expenses in a high-yield savings account</li>
          <li><strong>Max out retirement accounts</strong> - 401(k), Roth IRA, HSA</li>
          <li><strong>Save for other goals</strong> - House down payment, car, travel</li>
          <li><strong>Invest in taxable accounts</strong> - Build wealth beyond retirement accounts</li>
          <li><strong>Enjoy life!</strong> - You earned it. Budget for fun without guilt.</li>
        </ol>

        <div class="mt-4 p-4 bg-green-50 rounded-lg">
          <p class="text-green-800">
            <strong>The debt payoff mindset is powerful.</strong> Once you've proven you can pay off debt,
            you know you can achieve any financial goal. Use that same intensity toward building wealth.
          </p>
        </div>
      </section>

      <!-- CTA -->
      <div class="mt-8 p-6 bg-red-50 rounded-lg text-center">
        <h3 class="text-xl font-bold text-red-700 mb-2">Ready to Create Your Payoff Plan?</h3>
        <p class="text-gray-600 mb-4">Enter your debts and see exactly when you'll be debt-free:</p>
        <a href="/debt-payoff/" class="inline-block px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700">
          Debt Payoff Calculator
        </a>
      </div>
    </div>
  </article>
`,
};
